import { JoyInteractableMap, MAP_CARDS_PORTAL_ID, VenueMapPin } from '@shared/components/JoyInteractableMap';
import { JoyInteractableMapProvider, useJoyInteractableMapContext } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { Box, ButtonV2, Spinner } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useGoogleInteractableMap } from '@shared/components/JoyInteractableMap/JoyInteractableMap.utils';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { MapMarker } from './MapMarker';
import { ChevronLeftSquare, ChevronRightSquare } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { TelemetryProvider, useMapTelemetry } from './Map.telemetry';
import { AccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import React, { useEffect, useRef, useState } from 'react';
import { MapProps } from './Map.types';
import { LatLngLiteral } from '@shared/hooks/useGooglePlaceAPI';
import { useQueryParamHelper } from '@shared/core/queryString';
import { withWindow } from '@shared/utils/withWindow';
import { useGoogleMaps } from '@shared/hooks/useGoogleMaps';
import { MapDebug } from './MapDebug';
import { DEBOUNCE_TIMEOUT_MS, DEFAULT_RADIUS_KM, DEFAULT_ZOOM, MIN_SEARCH_ZOOM, PANEL_DISTANCE_MULTIPLIER, ZOOM_PAN_CONDITIONS } from './Map.constants';
import { useTranslation } from '@shared/core';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

const MapWithJoyInteractableMap = ({
  hotels,
  isFetchingHotels,
  location,
  venue,
  onChangeLocation,
  onChangeMapViewPort,
  onClickMarker,
  hidePanel,
  onToggleLeftPanel,
  reCenterMap,
  setReCenterMap,
  selectedMarker,
  mapMarkers,
  isCouplePick,
  shouldShowVenueMapPin = true,
}: MapProps) => {
  const [googleMaps] = useGoogleMaps();
  const { map } = useJoyInteractableMapContext();
  const { reduceBounds, getRadiusFromBounds, getCalculatedCenterAndZoom } = useGoogleInteractableMap();
  const telemetry = useMapTelemetry();
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const isMobileOrTablet = useIsMobileOrTablet();

  const { t } = useTranslation('accommodationsConsole');
  const mapTrans = t('map');

  const queryParamHelper = useQueryParamHelper();
  const debug = queryParamHelper.getValueString('debug') === 'true';

  const debounceZoom = useRef<number | undefined>();
  const debounceCenterPreviousCenter = useRef<number | undefined>();
  const isMapLoadedRef = useRef<boolean>(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const previousCenterRef = useRef<{ center: google.maps.LatLngLiteral; zoom?: number } | null>(null);

  const [activeMarker, setActiveMarker] = useState<string>();
  const [initialMapCenter, setInitialMapCenter] = useState<LatLngLiteral | null>(null);
  const [pendingCenterChange, setPendingCenterChange] = useState<{ center: google.maps.LatLngLiteral; bounds: google.maps.LatLngBounds }>();
  const [couplePickMapData, setCouplePickMapData] = useState<{ center: google.maps.LatLngLiteral; bounds: google.maps.LatLngBounds }>();

  // callback to parent on map change, to show places available in view port
  const callbackOnViewPortChange = useEventCallback(() => {
    if (!map) return;
    const bounds = map.getBounds();
    const center = map.getCenter();
    if (bounds && center) {
      onChangeMapViewPort?.({ lat: center.lat(), lng: center.lng() }, bounds);
    }
  });

  // Show/Hide "Search this Area" CTA
  const evaluateSearchAreaCtaVisibility = useEventCallback((updatedCenter?: google.maps.LatLngLiteral) => {
    if (!map || !isMapLoadedRef.current) return;

    const bounds = map.getBounds();
    const center = map.getCenter();
    const zoom = map.getZoom();
    const prev = previousCenterRef.current;

    if (!bounds || !center || zoom == null) return;

    // Hide CTA if zoom level is too low (continent/country view)
    // Only show for city-level and neighborhood searches (zoom ≥ 8)
    if (zoom < MIN_SEARCH_ZOOM) {
      setPendingCenterChange(undefined);
      callbackOnViewPortChange();
      return;
    }

    const currentCenter = { lat: center.lat(), lng: center.lng() };
    const zoomChanged = zoom !== prev?.zoom;

    // Case 1: Zoom change => show CTA
    if (zoomChanged) {
      setPendingCenterChange({ center: currentCenter, bounds });
    }
    // Case 2: Center moved significantly => show CTA
    else if (center && prev?.center && googleMaps) {
      const newCenter = updatedCenter || { lat: center.lat(), lng: center.lng() };
      const distance = googleMaps.geometry.spherical.computeDistanceBetween(new googleMaps.LatLng(newCenter), new googleMaps.LatLng(prev.center.lat, prev.center.lng));

      const shouldShow = ZOOM_PAN_CONDITIONS.some(c => zoom >= c.zoom && distance >= (hidePanel ? c.distance * PANEL_DISTANCE_MULTIPLIER : c.distance));

      setPendingCenterChange(shouldShow ? { center: newCenter, bounds } : undefined);
    }
    // Case 3: No significant change => hide CTA
    else {
      setPendingCenterChange(undefined);
    }

    callbackOnViewPortChange();
  });

  // Update previous center when click on "Search this Area" cta
  const persistPreviousCenter = useEventCallback((updatedCenter?: google.maps.LatLngLiteral) => {
    if (debounceCenterPreviousCenter.current) clearTimeout(debounceCenterPreviousCenter.current);
    withWindow(global => {
      debounceCenterPreviousCenter.current = global.setTimeout(() => {
        if (map) {
          const zoom = map.getZoom();
          const center = map.getCenter();
          if (center) {
            previousCenterRef.current = { center: { lat: center.lat(), lng: center.lng() }, zoom };
            setMapLoaded();
            evaluateSearchAreaCtaVisibility({ lat: center.lat(), lng: center.lng() });
          }
        } else if (updatedCenter) {
          previousCenterRef.current = { center: updatedCenter, zoom: DEFAULT_ZOOM };
          setMapLoaded();
          evaluateSearchAreaCtaVisibility(updatedCenter);
        }
      }, DEBOUNCE_TIMEOUT_MS);
    });
  });

  // Set map as loaded - Identifies whether the map has loaded after auto center and zoom
  const setMapLoaded = useEventCallback((isMapLoaded = true) => {
    isMapLoadedRef.current = isMapLoaded;
  });

  const handleZoomChanged = useEventCallback(() => {
    if (debounceZoom.current) clearTimeout(debounceZoom.current);
    withWindow(global => {
      debounceZoom.current = global.setTimeout(() => {
        telemetry.mapInteraction('zoom');
        evaluateSearchAreaCtaVisibility();
      }, DEBOUNCE_TIMEOUT_MS);
    });
  });

  const handleCenterChanged = useEventCallback((lat: google.maps.LatLngLiteral['lat'], lng: google.maps.LatLngLiteral['lng']) => {
    telemetry.mapInteraction('pan');
    evaluateSearchAreaCtaVisibility({ lat, lng });
  });

  // Calculate new location lat, lng and radius
  const calculateChangedLocation = useEventCallback((lat: google.maps.LatLngLiteral['lat'], lng: google.maps.LatLngLiteral['lng'], bounds?: google.maps.LatLngBounds) => {
    const reduced = bounds && reduceBounds ? reduceBounds(bounds, 0.1) : null;
    if (reduced && getRadiusFromBounds) {
      const { radius, center } = getRadiusFromBounds(reduced);
      return { location: { lat: center.lat(), lng: center.lng() }, radius: +radius };
    }
    return { location: { lat, lng }, radius: +DEFAULT_RADIUS_KM.toFixed(0) };
  });

  // callback to parent on click "Search this area"
  const handleSearchAreaClick = useEventCallback(() => {
    if (pendingCenterChange && map) {
      telemetry.searchThisAreaClicked();
      const { center, bounds } = pendingCenterChange;
      const newLocation = calculateChangedLocation(center.lat, center.lng, bounds);
      onChangeLocation?.(newLocation);
      persistPreviousCenter(newLocation.location);
      setPendingCenterChange(undefined);
    }
  });

  const handleMarkerClick = useEventCallback((hotel: AccommodationListingDTO) => {
    onClickMarker?.(hotel);
  });

  const handleMapClick = useEventCallback((e: MouseEvent) => {
    if (!mapContainerRef.current?.contains(e.target as Node)) {
      e.stopPropagation();
      setActiveMarker(undefined);
    }
  });

  const handleMapBoundsChanged = useEventCallback(() => {
    if (mapMarkers?.length) {
      const centerAndZoom = getCalculatedCenterAndZoom ? getCalculatedCenterAndZoom(mapMarkers) : undefined;
      if (centerAndZoom) {
        setCouplePickMapData(centerAndZoom);
      }
    }
  });

  useEffect(() => {
    if (selectedMarker) {
      setActiveMarker(selectedMarker);
    }
  }, [selectedMarker]);

  useEffect(() => {
    if (!isMobile) {
      document.addEventListener('click', handleMapClick);
      return () => document.removeEventListener('click', handleMapClick);
    }
    return () => {};
  }, [handleMapClick, isMobile]);

  useEffect(() => {
    if (!initialMapCenter && location.latlng) {
      // Setup map center as per provided location from parent
      setInitialMapCenter(location.latlng);
      persistPreviousCenter(location.latlng);
      if (!mapMarkers?.length) {
        setMapLoaded();
      }
    }
  }, [initialMapCenter, location.latlng, mapMarkers?.length, persistPreviousCenter, setMapLoaded]);

  const handleRecentMapMarkers = useEventCallback(() => {
    if (mapMarkers?.length) {
      handleMapBoundsChanged();
      setReCenterMap({ location: false, markers: false });
    }
  }, [mapMarkers]);

  const handleRecenterMapLocation = useEventCallback((isDefaultZoom?: boolean) => {
    if (map && location.latlng) {
      map.panTo(location.latlng);
      if (isDefaultZoom) {
        map.setZoom(DEFAULT_ZOOM);
      }
      persistPreviousCenter(location.latlng);
      setReCenterMap({ ...reCenterMap, location: false, isDefaultZoom: false });
    }
  });

  useEffect(() => {
    // Force update map center after load venue/event location
    if (reCenterMap.markers) {
      handleRecentMapMarkers();
      // Force update map center after load venue/event location
    } else if (reCenterMap.location) {
      handleRecenterMapLocation(reCenterMap.isDefaultZoom);
    }
  }, [reCenterMap, handleRecentMapMarkers, handleRecenterMapLocation]);

  useEffect(() => {
    if (couplePickMapData && isCouplePick) {
      map?.fitBounds(couplePickMapData.bounds);
      map?.setCenter(couplePickMapData.center);
      persistPreviousCenter(couplePickMapData.center);
    }
  }, [couplePickMapData, map, persistPreviousCenter, isCouplePick]);

  if (!initialMapCenter) return null;

  return (
    <Box
      ref={mapContainerRef}
      position="relative"
      width="100%"
      height="100%"
      flex={1}
    >
      {/* To debug center and bounds of the map */}
      {debug && googleMaps && map && (
        <MapDebug
          map={map}
          googleMaps={googleMaps}
        />
      )}
      <JoyInteractableMap
        center={(isCouplePick && couplePickMapData?.center ? couplePickMapData.center : initialMapCenter) as google.maps.LatLngLiteral}
        zoom={DEFAULT_ZOOM}
        gestureHandling="greedy"
        onZoomChanged={handleZoomChanged}
        onCenterChanged={handleCenterChanged}
        onClick={handleMapClick}
        isCenterChangedDebounced
      >
        {isFetchingHotels && (
          <Box
            position="absolute"
            left="50%"
            top={9}
            transform="translate(-50%, -50%)"
          >
            <Spinner />
          </Box>
        )}

        {pendingCenterChange && isMapLoadedRef.current && (
          <ButtonV2
            top={7}
            left="50%"
            shape="rounded"
            variant="outline"
            intent="neutral"
            backgroundColor={'white'}
            _hover={{ backgroundColor: 'mono2' }}
            _active={{ backgroundColor: 'mono2' }}
            _focus={{ backgroundColor: 'mono2' }}
            position="absolute"
            typographyVariant="button2"
            transform="translate(-50%, 0)"
            onClick={handleSearchAreaClick}
          >
            {mapTrans.searchCta()}
          </ButtonV2>
        )}

        {!isMobileOrTablet && (
          <ButtonV2
            onClick={onToggleLeftPanel}
            startIcon={hidePanel ? <ChevronRightSquare size={24} /> : <ChevronLeftSquare size={24} />}
            width={pxToRem(44)}
            height={pxToRem(44)}
            intent="neutral"
            backgroundColor="white"
            variant="ghost"
            color="mono14"
            top={7}
            left={0}
            borderRadius={0}
            borderTopRightRadius={3}
            borderBottomRightRadius={3}
          />
        )}

        {isMobile && (
          <Box
            id={MAP_CARDS_PORTAL_ID}
            position="fixed"
            bottom={0}
            left={0}
            width="100%"
            padding={5}
          />
        )}

        {hotels?.map((hotel, idx) => (
          <MapMarker
            key={`${idx}-${hotel.id}`}
            hotel={hotel}
            isHighlighted={activeMarker === hotel.joyPlaceId}
            activeMarker={activeMarker}
            onClickMarker={handleMarkerClick}
            isHoveringAddedHotel={false}
          />
        ))}
        {venue?.location && shouldShowVenueMapPin && <VenueMapPin position={venue.location} />}
      </JoyInteractableMap>
    </Box>
  );
};

export const Map = (props: MapProps) => (
  <JoyInteractableMapProvider>
    <TelemetryProvider>
      <MapWithJoyInteractableMap {...props} />
    </TelemetryProvider>
  </JoyInteractableMapProvider>
);
